import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { track } from '../../third-party/MixPanel';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  createTheme,
  ThemeProvider,
  Alert,
  IconButton,
} from '@mui/material';

// If you have brand icons from somewhere else, import them; otherwise, placeholders:
import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// Import your lotus logo asset
import lumaLogo from '../../assets/luma-logo.png';
const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#A86E64',
    },
    background: {
      default: '#ECEAE5',
      paper: 'transparent',
    },
  },
});

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [attempt, setAttempt] = useState(1);
  const [error, setError] = useState('');
  const { login, loginWithCredentials } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Send the access token to your backend
        const response = await axiosInstance.post('/api/auth/google', {
          access_token: tokenResponse.access_token,
        });
        // Store token in localStorage and handle successful Google login flow
        localStorage.setItem('token', response.data.token);
        login(response.data.token);
        navigate('/home');
      } catch (err) {
        console.error('Google login error:', err);
        setError(t('login.errorGoogleLogin'));
      }
    },
    onError: () => {
      setError(t('login.errorGoogleLoginGeneric'));
    },
  });

  // Placeholder function for Apple login
  const handleAppleLogin = () => {
    // Implement Apple login logic here
    console.log('Apple login not implemented');
  };

  // Placeholder function for Facebook login
  const handleFacebookLogin = () => {
    // Implement Facebook login logic here
    console.log('Facebook login not implemented');
  };

  const loginAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError(t('login.errorFillFields'));
      return;
    }

    setError('');

    try {
      await loginWithCredentials(email, password);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || t('login.errorInvalidCredentials'));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', pt: 4, pb: 4 }}>
          <Card sx={{ width: '100%', boxShadow: 0, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box display="flex" justifyContent="center" mb={2}>
                <img
                  src={lumaLogo}
                  alt="Luma Logo"
                  style={{ maxWidth: 80, height: 'auto' }}
                />
              </Box>

              <Typography variant="h5" component="h1" textAlign="center" gutterBottom>
                {t('login.title')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Email Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('login.emailLabel')}
                type="email"
                autoComplete="email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  track('login_started', { method: 'email' });
                }}
              />
              {/* Password Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('login.passwordLabel')}
                type={showPassword ? "text" : "password"}
                autoComplete="on"
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              {/* Sign In Button */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, mb: 2 }}
                onClick={loginAuth}
              >
                {t('login.signInButton')}
              </Button>

              <Box mt={2} mb={2} textAlign="center">
                <Typography variant="body2" color="textSecondary" sx={{ display: 'none' }}>
                  {t('login.orSignInWith')}
                </Typography>
              </Box>

              <Stack spacing={1}>
                {/* Google Sign-In */}
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  startIcon={<GoogleIcon />}
                  onClick={() => googleLogin()}
                  sx={{ display: 'none' }}
                >
                  {t('login.signInWithGoogle')}
                </Button>

                {/* Apple Sign-In */}
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  startIcon={<AppleIcon />}
                  onClick={handleAppleLogin}
                  sx={{ display: 'none' }}
                >
                  {t('login.signInWithApple')}
                </Button>

                {/* Facebook Sign-In */}
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  startIcon={<FacebookIcon />}
                  onClick={handleFacebookLogin}
                  sx={{ display: 'none' }}
                >
                  {t('login.signInWithFacebook')}
                </Button>
              </Stack>

              <Box mt={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {t('login.noAccount')}{' '}
                  <Link to="/register" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                    {t('login.register')}
                  </Link>
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {t('login.forgotPassword')}{' '}
                  <Link to="/forgot-password" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                    {t('login.resetHere')}
                  </Link>
                </Typography>
              </Box>

            </CardContent>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
